// src/features/checkout/components/CheckoutSuccess.tsx

import React, { useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderService } from '../../setting/services/orderService';
import { trackPurchase } from '../../../services/analytics/analytics';

const PAID_STATUSES = ['processing', 'completed'];
const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 20;

const purchaseTrackedKey = (orderId: string) => `ga_purchase_tracked_${orderId}`;

const wasTracked = (orderId: string) => {
  try {
    return !!localStorage.getItem(purchaseTrackedKey(orderId));
  } catch {
    return false;
  }
};

const markTracked = (orderId: string) => {
  try {
    localStorage.setItem(purchaseTrackedKey(orderId), '1');
  } catch {
    // no-op
  }
};

export const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');

  // postback จาก PaySolutions อาจมาช้ากว่าที่ลูกค้ากลับเข้าหน้านี้ จึง poll จนกว่า order จะถูก mark ว่าจ่ายแล้ว
  useEffect(() => {
    if (!orderId || wasTracked(orderId)) return;

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      try {
        const order = await orderService.getOrderById(orderId);
        if (cancelled) return;
        if (PAID_STATUSES.includes(order.status)) {
          trackPurchase(
            String(order.order_id),
            order.currency,
            Number(order.total),
            order.items.map((i) => ({
              item_id: i.product_id,
              item_name: i.name,
              price: Number(i.price),
              quantity: i.quantity,
            })),
          );
          markTracked(orderId);
          return;
        }
      } catch {
        // no-op
      }
      attempts += 1;
      if (!cancelled && attempts < MAX_ATTEMPTS) {
        timer = setTimeout(check, POLL_INTERVAL_MS);
      }
    };

    check();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      {/* Logo */}
      <Box sx={{ mb: 3, width: '160px', justifySelf: 'center' }}>
        <img
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          src={`${process.env.REACT_APP_HOME_URL}/wp-content/uploads/2026/02/be7f292740925e2dba8d7585a76b26e16bec7f2a.png`}
          alt=""
        />
      </Box>

      {/* Heading */}
      <Typography variant="h4" sx={{ mb: 2, color: '#E91E63', fontWeight: 'normal' }}>
        Thank You for Your Order
      </Typography>

      {/* Message */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Your order has been placed successfully and your payment is complete.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        You can track your order status and view order details at any time on the My Orders page.
      </Typography>

      {/* Buttons */}
      <Button
        variant="contained"
        onClick={() => navigate('/my-orders')}
        sx={{ mt: 3, mr: 2, bgcolor: '#000', color: '#fff', '&:hover': { bgcolor: '#333' }, textTransform: 'none', px: 4, py: 1.5 }}
      >
        My Orders
      </Button>

      <Button
        variant="contained"
        onClick={() => navigate('/')}
        sx={{ mt: 3, color: '#000', bgcolor: '#fff', '&:hover': { bgcolor: '#eee' }, textTransform: 'none', px: 4, py: 1.5 }}
      >
        Continue Shopping
      </Button>
    </Box>
  );
};
